/* eslint-disable no-restricted-syntax -- the rule points callers at handleError; this module defines it */
import { getAuth } from 'firebase/auth'
import LogRocket from 'logrocket'
import { toast } from 'react-toastify'

import { FIREBASE_OBJECT_NOT_FOUND, ERROR_EXCEPTIONS } from 'settings/error'
import T from 'T'

const { FETCH_ERROR } = ERROR_EXCEPTIONS

/** The fields these handlers read off whatever a caller caught — an RTKQ payload, an SDK error, an Error. */
interface CaughtError {
  code?: string
  status?: string | number
  originalStatus?: number
  message?: string
  cause?: unknown
  data?: { message?: string; error?: string }
}

const asCaughtError = (error: unknown): CaughtError => (typeof error === 'object' && error !== null ? (error as CaughtError) : {})

// Firestore rejects a read identically whether the data-project identity was never established or
// names a different tenant, and nothing recorded which. Set by WithFirebase; read on rejection.
let firestoreMintState = 'not-attempted'

/** Records how the Firestore data-project identity was obtained, for a later denied-read report. */
export const recordFirestoreMintState = (state: string) => {
  firestoreMintState = state
}

/** Reads the user-facing message off a rejected RTKQ payload, an SDK error or a thrown Error. */
export const getErrorMessage = (error: unknown): string | undefined => {
  const { data, message } = asCaughtError(error)
  return data?.message || data?.error || message
}

/** LogRocket records an Error and drops anything else — a rejected payload has to be wrapped first. */
const toException = (error: unknown): Error => (error instanceof Error ? error : new Error(getErrorMessage(error) || String(error)))

// Once per page load; a screen holds several listeners that would each report the same thing.
let deniedReadReported = false

const captureDeniedFirestoreRead = async (error: unknown) => {
  if (deniedReadReported) return
  deniedReadReported = true

  try {
    const user = getAuth().currentUser
    const extra = { mintState: firestoreMintState, tenants: '' }
    if (user) {
      // The one thing a present identity can still get wrong: a claim for a different tenant. Reading
      // it can fail on its own, and mintState still has to go out when it does.
      try {
        const { claims } = await user.getIdTokenResult()
        extra.tenants = String(claims?.tenants ?? '')
      } catch {
        extra.tenants = 'claims-unreadable'
      }
    }
    LogRocket.captureException(toException(error), { tags: { firestoreIdentity: user ? 'present' : 'missing' }, extra })
  } catch (diagnosticError) {
    // Release the one shot: a send that failed has told us nothing, so a later rejection should
    // still get to report.
    deniedReadReported = false
    console.error('[firestore] could not describe the identity behind a denied read:', diagnosticError)
  }
}

/** True when RTKQ could not reach the API at all, as opposed to the API answering with an error. */
export const getIsFetchError = (error: unknown): boolean => asCaughtError(error).status === FETCH_ERROR

/** True when the API answered that the resource is gone. */
export const getIsNotFound = (error: unknown): boolean => asCaughtError(error).status === 404

const GATEWAY_TIMEOUT_STATUSES = [502, 504]

/**
 * True when the load balancer gave up waiting on the API. It answers a backend timeout with a 502 as
 * well as a 504, in HTML, so RTKQ reports it as a PARSING_ERROR carrying `originalStatus`.
 */
export const getIsGatewayTimeout = (error: unknown): boolean => {
  const { status, originalStatus } = asCaughtError(error)
  return GATEWAY_TIMEOUT_STATUSES.some(code => code === status || code === originalStatus)
}

/** Toasts the message the error carries, if it has one meant for the user. */
export const handleError = (error: unknown) => {
  // Firestore's own code; our API surfaces permission failures as exceptionType instead.
  if (asCaughtError(error).code === 'permission-denied') captureDeniedFirestoreRead(error)

  const errorMsg = getErrorMessage(error)
  if (errorMsg) {
    // toastId de-dupes against the global PERMISSION_DENIED toast in rtkQueryErrorLogger so a
    // permission-blocked write that is also caught locally shows a single toast, not two.
    toast.error(errorMsg, { toastId: errorMsg })
  }
}

/**
 * Toasts `message` and reports `error` to LogRocket. Use where the error is a raw JS or SDK failure:
 * handleError would toast its developer text ("Unexpected token ']'") and leave no other trace.
 */
export const handleErrorWithMessage = (error: unknown, message: string) => {
  // Wrappers like getGcsJsonData keep the real failure on `cause`, which nothing reads today.
  const { cause } = asCaughtError(error)
  LogRocket.captureException(toException(error), { extra: { cause: cause ? String(asCaughtError(cause).message ?? cause) : '' } })
  toast.error(message, { toastId: message })
}

/**
 * Reports `error` with no toast, for a failure the screen already names — without it the only
 * record is the message on that one user's screen, which reaches nobody on call.
 */
export const captureError = (error: unknown, extra: Record<string, string | number | boolean> = {}) => {
  LogRocket.captureException(toException(error), { extra })
  // LogRocket only initialises outside dev, so without this the failure leaves no trace locally.
  console.error('[captureError]', extra, error)
}

/** Toasts `message` when the API was unreachable, and the error's own message otherwise. */
export const handleErrorWithFetchError = (
  error: unknown,
  message = 'Something went wrong while processing the request. Please try again.'
) => {
  if (getIsFetchError(error)) {
    toast.error(message)
    return
  }
  handleError(error)
}

/** Toasts a Firebase Storage failure, mapping the codes worth naming to the user. */
export const cloudStorageErrorHandler = (error: unknown) => {
  // A full list of error codes is available at
  // https://firebase.google.com/docs/storage/web/handle-errors
  const { code } = asCaughtError(error)
  if (code) {
    switch (code) {
      case FIREBASE_OBJECT_NOT_FOUND:
      case 'storage/unauthorized':
        return toast.error('File not found')
      case 'storage/canceled':
        return undefined
      case 'storage/unknown':
      default:
        return toast.error('There was an unexpected error. If the problem persists, contact support.')
    }
  }
  return handleError(error)
}

/** Maps a Firebase Auth failure to the copy shown next to the field that caused it. */
export const firebaseErrorHandler = (error: unknown): string => {
  const { code, message } = asCaughtError(error)
  if (code) {
    switch (code) {
      case 'auth/wrong-password':
        return T.CURRENT_PWD_INCORRECT
      case 'auth/weak-password':
        return T.WEAK_PASSWORD_ERROR
      default:
        return message || T.UNEXPECTED_ERROR
    }
  }
  return T.UNEXPECTED_ERROR
}
